import React, { DragEvent, ReactNode, useMemo, useRef } from "react";

import { StriimTooltip } from "@striim/striim-ui-v2";
import { Box } from "@mui/material";
import { useInView } from "react-intersection-observer";

import { Styles, StylesTheme } from "../../../../../../src/utils/types";
import { componentDnDDataTransfer } from "./componentDnD.utils";
import App from "../../../../../../app";
import licenseService from "../../../../../../core/services/metaStoreService/license-service";
import EllipsisText from "../../../../../../src/generic/ellipsis-text/ellipsis-text";
import AdapterNativeIcon from "../../../../../../src/generic/icon/component-icon/adapter-native-icon";
import componentService, {
    componentID,
    ComponentKeys
} from "../../../../../../core/services/componentService/component-service";
import componentNames from "../../../../../../resources/generated/components.json";

interface ComponentDnDProps {
    type: string;
    id: string;
    name: string;
    disabled?: boolean;
    customDisableReason?: ReactNode | string | null;
    scrollableRef?: React.RefObject<HTMLDivElement>;
    initialInView?: boolean;
}

const ComponentDnD = ({
    name,
    type,
    id,
    disabled,
    customDisableReason,
    scrollableRef,
    initialInView
}: ComponentDnDProps) => {
    const dndVisibleRef = useRef<HTMLDivElement>(null);
    const dndDraggableRef = useRef<HTMLDivElement>(null);

    const { ref: visibleRef, inView } = useInView({
        root: scrollableRef?.current,
        initialInView: initialInView,
        rootMargin: "300px", // Prerender when scroll is close to this component
        fallbackInView: true
    });

    const sxMemo = useMemo(
        () => ({
            draggable: disabled ? styles.draggableDisabled : styles.draggableActive,
            child: [styles.childCommon, disabled ? styles.childDisabled : styles.childActive],
            onDragChild: [styles.childCommon, styles.childOnDrag, disabled ? styles.childDisabled : styles.childActive]
        }),
        [disabled]
    );

    const disableReason = useMemo(() => {
        if (!disabled) return "";
        if (customDisableReason) return customDisableReason;

        // Copy of ComponentView onRender
        if (["output-Databricks", "output-Fabric-Data-Warehouse"].indexOf(id) !== -1 && licenseService.isLiteLicense) {
            return "Requires Standard Plan. Go to Azure Marketplace to upgrade.";
        }

        return "Enabling the selected adapter requires additional license, please contact support@striim.com";
    }, [disabled, id]);

    const ComponentData = useMemo(() => {
        let componentInfo = componentService.getMetadata(id as componentID);
        if (!componentInfo) {
            console.error(`Not found icon for "${id}". Use default`);
            componentInfo = componentNames.BaseStream as any;
        }
        return (
            <>
                <AdapterNativeIcon
                    name={componentInfo[ComponentKeys.ComponentName] || ""}
                    type={componentInfo[ComponentKeys.Type] || ""}
                    size={32}
                    addWrapper
                    spriteId={componentInfo[ComponentKeys.SpriteID]}
                    newlyReleased={componentInfo[ComponentKeys.NewlyReleased] === "Yes"}
                    showBorder={false}
                />

                <EllipsisText text={name} sx={styles.text} data-testid="component-name" />
            </>
        );
    }, [id, styles.text, name]);

    return (
        <Box ref={visibleRef} sx={styles.root}>
            {inView ? (
                <>
                    <StriimTooltip title={disableReason} placement="bottom" arrow>
                        <Box
                            ref={dndVisibleRef}
                            sx={sxMemo.draggable}
                            data-testid="component-draggable"
                            data-name={name}
                            data-testkey={id}
                            draggable={!disabled}
                            onDragStart={
                                disabled
                                    ? undefined
                                    : e =>
                                          handleDragStart(e, {
                                              dndDraggableRef,
                                              dndVisibleRef,
                                              type,
                                              name,
                                              componentId: id
                                          })
                            }
                            onDragEnd={
                                disabled
                                    ? undefined
                                    : () =>
                                          handleStop({
                                              dndDraggableRef
                                          })
                            }
                        >
                            <Box sx={sxMemo.child} data-testid="component-child">
                                {ComponentData}
                            </Box>
                        </Box>
                    </StriimTooltip>

                    <Box
                        data-testid="component-on-drag"
                        ref={dndDraggableRef}
                        sx={sxMemo.onDragChild} /* Use preview element to correct render rounded corners */
                    >
                        {ComponentData}
                    </Box>
                </>
            ) : null
            /*<Box sx={styles.notInView}></Box>*/
            }
        </Box>
    );
};

export default ComponentDnD;

const styles: Styles = {
    root: {
        height: 54 // Manual calculate for InView (lazy render on scrolling), to prevent scroll shaking
    },
    /*notInView: {
        backgroundColor: "red", // For debug
        width: "100%",
        height: "100%"
    },*/

    draggableActive: {
        cursor: "move",

        "&:hover, &:focus": {
            backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[50]
        },
        "&:active": {
            backgroundColor: "transparent"
        }
    },
    draggableDisabled: {
        opacity: "0.5",
        cursor: "not-allowed",

        "&:hover, &:focus, &:active": {
            backgroundColor: "transparent",
            borderColor: "transparent",
            boxShadow: "none"
        }
    },

    childCommon: {
        p: 1,

        mx: 1,

        display: "flex",
        alignItems: "center",
        gap: 1,
        alignSelf: "stretch",

        userSelect: "none",

        borderRadius: "12px",
        border: "1px solid transparent"
    },
    childActive: {
        cursor: "move",

        "&:active": {
            backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[200],
            borderColor: ({ palette }: StylesTheme) => palette.greyscale[300],
            boxShadow: "0px 6px 15px 0px rgba(72, 82, 92, 0.12)"
        }
    },
    childOnDrag: {
        display: "none", // Hiding by default because setDragImage renders not the element, but box area and few components in the same position can overlap. Changing on start dragging

        position: "absolute",
        top: "-9999px",
        width: 240,

        cursor: "move",

        zIndex: 99999,
        backgroundColor: ({ palette }: StylesTheme) => palette.greyscale[200],
        borderColor: ({ palette }: StylesTheme) => palette.greyscale[300]
        // Not possible to add boxShadow because of overlap. Using browser's native from drag event
    },
    childDisabled: {},

    text: {
        color: ({ palette }: StylesTheme) => palette.greyscale[900],
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: "Inter",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "20px" /* 142.857% */,
        letterSpacing: "0.14px",
        textTransform: "capitalize"
    }
};

const handleDragStart = (
    e: DragEvent<HTMLDivElement>,
    {
        dndDraggableRef,
        dndVisibleRef,
        type,
        name,
        componentId
    }: {
        dndDraggableRef: React.RefObject<HTMLDivElement>;
        dndVisibleRef: React.RefObject<HTMLDivElement>;
        type: string;
        name: string;
        componentId: string;
    }
) => {
    e.dataTransfer.effectAllowed = "all";
    e.dataTransfer.dropEffect = "copy";

    e.dataTransfer.setData(componentDnDDataTransfer.type, type);
    e.dataTransfer.setData(componentDnDDataTransfer.name, name);
    e.dataTransfer.setData(componentDnDDataTransfer.componentId, componentId);

    dndDraggableRef.current.style.display = "flex";

    const rect = dndVisibleRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    e.dataTransfer.setDragImage(dndDraggableRef.current, offsetX, offsetY);

    App.vent.trigger("fd:ls:on-drag-start", { type, name });
};

const handleStop = ({ dndDraggableRef }: { dndDraggableRef: React.RefObject<HTMLDivElement> }) => {
    dndDraggableRef.current.style.display = "none"; // Fix reuse dragging of same components
    App.vent.trigger("fd:ls:on-drag-stop");
};
